import { useSelectQuery } from "@smartrent/ui";

import { createAxiosQuery, createAxiosMutation } from "@/hooks/react-query";

import { instance } from "@/lib/hooks";

export interface AlloyAccessOrgRecord {
  id: number;
  name: string;
}

export interface AlloyAccessSiteRecord {
  id: number;
  name: string;
  organization_id: number;
  system_type: string;
  timezone: string;
  site_url: string;
}

interface AlloyAccessOrgsResponse {
  records: AlloyAccessOrgRecord[];
  total_records: number;
  total_pages: number;
  current_page: number;
  per_page: number;
}

interface AlloyAccessSitesResponse {
  records: AlloyAccessSiteRecord[];
  total_records: number;
  total_pages: number;
  current_page: number;
  per_page: number;
}

export const useAlloyAccessOrgsSelectProps = (groupId: number) =>
  useSelectQuery<AlloyAccessOrgsResponse>(
    (inputValue) => ["alloy-access-orgs", groupId, inputValue] as const,
    function fetch({ queryKey, pageParam = 1 }) {
      const [, , inputValue] = queryKey;
      return instance
        .get(`/groups/${groupId}/alloy-access/organizations`, {
          params: {
            ...(inputValue ? { name: inputValue } : {}),
            page: pageParam,
          },
        })
        .then((r) => r.data);
    },
    {},
    { inputDebounceInterval: 250 }
  );

export const useAlloyAccessSitesSelectProps = (
  groupId: number,
  organizationId: number | null,
  systemType: "alloy_access" | "salto"
) =>
  useSelectQuery<AlloyAccessSitesResponse>(
    (inputValue) =>
      [
        "alloy-access-sites",
        groupId,
        organizationId,
        systemType,
        inputValue,
      ] as const,
    function fetch({ queryKey, pageParam = 1 }) {
      const [, , , , inputValue] = queryKey;
      return instance
        .get(`/groups/${groupId}/alloy-access/sites`, {
          params: {
            organization_id: organizationId,
            system_type: systemType,
            ...(inputValue ? { name: inputValue } : {}),
            page: pageParam,
          },
        })
        .then((r) => r.data);
    },
    { enabled: organizationId !== null },
    { inputDebounceInterval: 250 }
  );

export interface AlloyAccessSiteQueryFilters {
  groupId: number;
  siteId: string | null;
}

export const useAlloyAccessSiteQuery = createAxiosQuery(
  "alloy-access-site",
  async (filters?: AlloyAccessSiteQueryFilters) => {
    const { groupId, siteId } = filters!;
    return instance.get<AlloyAccessSitesResponse>(
      `/groups/${groupId}/alloy-access/sites`,
      { params: { id: siteId } }
    );
  }
);

type LinkAlloyAccessSiteVariables = { groupId: number } & (
  | { site_id: number | string; system_type: string }
  | {
      name: string;
      organization_id: number;
      timezone: string;
      system_type: string;
    }
);

export const useLinkAlloyAccessSiteMutation = createAxiosMutation(
  async ({ groupId, ...body }: LinkAlloyAccessSiteVariables) => {
    return instance.post<{ site_id: string }>(
      `/groups/${groupId}/alloy-access/site`,
      body
    );
  },
  {
    onSuccess: (queryClient, _data, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: ["group", groupId] });
      queryClient.invalidateQueries({ queryKey: ["tpp-view"] });
    },
  }
);

interface ToggleAlloyAccessIntegrationVariables {
  groupId: number;
  enabled: boolean;
  system_type: "alloy_access" | "salto";
}

export const useToggleAlloyAccessIntegrationMutation = createAxiosMutation(
  async ({
    groupId,
    enabled,
    system_type,
  }: ToggleAlloyAccessIntegrationVariables) => {
    return instance.patch<{ enabled: boolean }>(
      `/groups/${groupId}/alloy-access/integration`,
      { enabled, system_type }
    );
  },
  {
    onSuccess: (queryClient) => {
      queryClient.invalidateQueries({ queryKey: ["tpp-view"] });
    },
  }
);
